/**
 * RawEventAppendix - Lists all journal entries chronologically for audit purposes.
 *
 * Displays a table of all raw events (input, output, note) sorted by recordedAt.
 * Each row shows the event id, category, recorded timestamp, and summary.
 * A collapsible details section allows expanding to view the full rawPayload JSON.
 *
 * Requirements: 17.3
 */

import React, { useState, useCallback } from 'react';
import type { RawEventEntry } from '../types';

export interface RawEventAppendixProps {
  events: RawEventEntry[];
}

/** Column headers for the raw event table */
const COLUMN_HEADERS = ['ID', 'Category', 'Recorded At', 'Summary', 'Details'];

/**
 * Renders a single expandable row for a raw event entry.
 */
const RawEventRow: React.FC<{ event: RawEventEntry }> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <tr>
        <td
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            verticalAlign: 'top',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
        >
          {event.id}
        </td>
        <td
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            verticalAlign: 'top',
            whiteSpace: 'nowrap',
            textTransform: 'capitalize',
          }}
        >
          {event.category}
        </td>
        <td
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            verticalAlign: 'top',
            whiteSpace: 'nowrap',
            fontSize: '0.9em',
          }}
        >
          {event.recordedAt}
        </td>
        <td
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            verticalAlign: 'top',
          }}
        >
          {event.summary}
        </td>
        <td
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            verticalAlign: 'top',
            textAlign: 'center',
          }}
        >
          <button
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse raw payload' : 'Expand raw payload'}
            style={{
              background: 'none',
              border: '1px solid #999',
              borderRadius: '4px',
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: '0.85em',
            }}
          >
            {expanded ? '▼ Hide' : '▶ Show'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td
            colSpan={5}
            style={{
              border: '1px solid #ccc',
              padding: '8px 16px',
              backgroundColor: '#f9f9f9',
            }}
          >
            <pre
              style={{
                margin: 0,
                fontSize: '0.8em',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(event.rawPayload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const RawEventAppendix: React.FC<RawEventAppendixProps> = ({ events }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (events.length === 0) {
    return (
      <div className="raw-event-appendix raw-event-appendix--empty" style={{ padding: '1rem' }}>
        <p>No journal entries recorded for this month.</p>
      </div>
    );
  }

  return (
    <div className="raw-event-appendix">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: '0.5rem 0',
          marginBottom: isExpanded ? '0.5rem' : '0',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        aria-expanded={isExpanded}
        aria-label="Toggle raw event appendix"
      >
        <p style={{ fontSize: '0.85em', color: '#666', margin: 0 }}>
          {isExpanded ? '▼' : '▶'} Raw Data — {events.length} journal {events.length === 1 ? 'entry' : 'entries'} recorded
        </p>
      </div>
      {isExpanded && (
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}
        aria-label="Raw event appendix"
      >
        <thead>
          <tr>
            {COLUMN_HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                style={{
                  border: '1px solid #ccc',
                  padding: '8px',
                  backgroundColor: '#f5f5f5',
                  textAlign: 'left',
                  verticalAlign: 'top',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <RawEventRow key={event.id} event={event} />
          ))}
        </tbody>
      </table>
      )}
    </div>
  );
};

export default RawEventAppendix;
